import { useCallback, useMemo } from 'react';
import { Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatFullName } from 'shared/utils';
import { ROUTES } from 'shared/constants/routes';
import {
  AG_FILTER_TYPES,
  SetFilter,
  setFilterDoesFilterPass,
  Table,
  textWithFallback,
} from 'src/components/design-system/table';
import { useProfileLang } from 'shared/hooks/profile.hooks';
import { attendanceModeLabel, getCheckInMethodLabel } from '../../helpers/event-view.helpers';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import styles from './check-in.module.scss';

const ATTENDEES_PAGE_SIZE = 10;
const ATTENDEES_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SET_FILTER = { component: SetFilter, doesFilterPass: setFilterDoesFilterPass };
const RESETTABLE_FILTER = { buttons: ['reset'], debounceMs: 0 };

export function CheckedInList({ items, users, onGridReady, onSelectUser }) {
  const attendeesLang = useSelector(getLang('CHECKED_IN_ATTENDEES'));
  const eventsLang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const navigate = useNavigate();
  const { getValueFromLang } = useProfileLang();
  const { formatDateWithTime } = useEducationalEventFormat();

  const handleRowClicked = useCallback(
    ({ data }) => {
      const userId = data?.user?.id;
      if (!userId) return;
      if (onSelectUser) {
        onSelectUser(userId);
      } else {
        navigate(ROUTES.CONTACTS_PREVIEW.replace(':id', userId));
      }
    },
    [navigate, onSelectUser],
  );

  const usersById = useMemo(() => Object.fromEntries(users.map(user => [user.id, user])), [users]);

  const rowData = useMemo(
    () =>
      items
        .filter(item => usersById[item.userId])
        .map(item => ({ ...item, user: usersById[item.userId] })),
    [items, usersById],
  );

  const columns = useMemo(
    () => [
      {
        headerName: attendeesLang.COLUMN_NAME,
        colId: 'name',
        sortable: true,
        valueGetter: params => (params.data?.user ? formatFullName(params.data.user) : null),
        valueFormatter: textWithFallback,
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
        comparator: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
      },
      {
        headerName: attendeesLang.COLUMN_ROLE,
        colId: 'role',
        sortable: true,
        valueGetter: params => getValueFromLang(params.data?.user?.professional?.role),
        valueFormatter: textWithFallback,
        filter: SET_FILTER,
        comparator: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
      },
      {
        headerName: attendeesLang.COLUMN_TYPE,
        colId: 'attendanceMode',
        sortable: true,
        valueGetter: params => params.data?.attendanceMode,
        cellRenderer: params =>
          params.value ? (
            <Tag className={styles.typeTag} variant="filled">
              {attendanceModeLabel(params.value, eventsLang)}
            </Tag>
          ) : (
            '-'
          ),
        filter: SET_FILTER,
        filterParams: { valueFormatter: value => attendanceModeLabel(value, eventsLang) },
      },
      {
        headerName: attendeesLang.COLUMN_CHECK_IN_TIME,
        colId: 'checkedInAt',
        sortable: true,
        field: 'checkedInAt',
        flex: 1,
        cellRenderer: params => (params.value ? formatDateWithTime(params.value) : '-'),
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
        filterValueGetter: params =>
          params.data?.checkedInAt ? formatDateWithTime(params.data.checkedInAt) : '',
      },
      {
        headerName: eventsLang.CHECK_IN_METHOD,
        colId: 'method',
        sortable: true,
        valueGetter: params => params.data?.method,
        cellRenderer: params => {
          const label = getCheckInMethodLabel({ method: params.value, lang: eventsLang });
          return label ? (
            <Tag className={styles.typeTag} variant="filled">
              {label}
            </Tag>
          ) : (
            '-'
          );
        },
        filter: SET_FILTER,
        filterParams: {
          valueFormatter: value => getCheckInMethodLabel({ method: value, lang: eventsLang }),
        },
      },
    ],
    [attendeesLang, eventsLang, getValueFromLang, formatDateWithTime],
  );

  if (!items.length) {
    return null;
  }

  return (
    <Table
      rootClassName={styles.attendeesTableRoot}
      containerProps={{ className: styles.attendeesTable }}
      columnDefs={columns}
      rowData={rowData}
      getRowId={params => params.data.id || `${params.data.userId}-${params.data.checkedInAt}`}
      enableActiveFilters
      pagination
      paginationPageSize={ATTENDEES_PAGE_SIZE}
      paginationPageSizeSelector={ATTENDEES_PAGE_SIZE_OPTIONS}
      domLayout="autoHeight"
      rowClass={styles.attendeeRowClickable}
      onRowClicked={handleRowClicked}
      onGridReady={params => onGridReady?.(params.api)}
    />
  );
}
